import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { useToast } from '../../hooks/use-toast';
import ImageUpload from '../../components/ImageUpload';
import { Palette, Image as ImageIcon, Type, RefreshCw } from 'lucide-react';

const BRAND_COLORS = [
  { name: 'Creme Suave', value: '#F5F1E8' },
  { name: 'Dourado', value: '#E6D6B8' },
  { name: 'Dourado Escuro', value: '#D4C4A0' },
  { name: 'Verde Principal', value: '#1E4D2B' },
  { name: 'Verde Escuro', value: '#163822' },
  { name: 'Texto', value: '#333333' },
];

const SECTIONS = [
  { id: 'hero', name: 'Hero (Topo)' },
  { id: 'intro', name: 'Introdução' },
  { id: 'about', name: 'Sobre' },
  { id: 'stats', name: 'Estatísticas' },
  { id: 'services', name: 'Serviços' },
  { id: 'regions', name: 'Regiões' },
  { id: 'testimonials', name: 'Depoimentos' },
  { id: 'blog', name: 'Blog' },
  { id: 'contact', name: 'Contacto' },
  { id: 'footer', name: 'Rodapé' },
];

const ThemeCustomization = () => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [activeSection, setActiveSection] = useState('hero');
  const [theme, setTheme] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/admin/login');
      return;
    }
    if (!authLoading && isAuthenticated) {
      loadTheme();
    }
  }, [isAuthenticated, authLoading, navigate]);

  const loadTheme = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/theme`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setTheme(data);
    } catch (error) {
      console.error('Error loading theme:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSectionTheme = (sectionId) => {
    return theme[sectionId] || {
      backgroundType: 'gradient',
      gradientColor1: '#F5F1E8',
      gradientColor2: '#1E4D2B',
      gradientDirection: 'to-br',
      backgroundOpacity: 100,
      overlayOpacity: 50,
      titleFont: 'Playfair Display',
      textFont: 'serif',
    };
  };

  const updateSectionTheme = (sectionId, updates) => {
    setTheme(prev => ({
      ...prev,
      [sectionId]: {
        ...getSectionTheme(sectionId),
        ...updates
      }
    }));
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/theme`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ id: 'theme', ...theme })
      });

      if (response.ok) {
        toast({
          title: 'Sucesso!',
          description: 'Tema salvo com sucesso. Recarregue o site para ver as mudanças.'
        });
      }
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Erro ao salvar tema',
        variant: 'destructive'
      });
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Tem certeza que deseja resetar o tema para o padrão?')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/theme/reset`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: 'Tema resetado',
        description: 'O tema foi restaurado para o padrão'
      });
      
      loadTheme();
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Erro ao resetar tema',
        variant: 'destructive'
      });
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Personalizar Tema">
        <p>Carregando...</p>
      </AdminLayout>
    );
  }

  const currentSectionTheme = getSectionTheme(activeSection);

  return (
    <AdminLayout title="Personalizar Tema">
      <div className="space-y-6">
        {/* Header com botões de ação */}
        <div className="flex justify-between items-center">
          <p className="text-slate-600">Customize cores, fontes e imagens de cada seção do site</p>
          <div className="flex gap-3">
            <Button variant="outline" onClick={handleReset}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Resetar
            </Button>
            <Button onClick={handleSave} className="bg-brand-primary hover:bg-brand-primary-hover text-white">
              Salvar Alterações
            </Button>
          </div>
        </div>

        {/* Seletor de Seção */}
        <Card>
          <CardHeader>
            <CardTitle>Escolha a Seção para Personalizar</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {SECTIONS.map(section => (
                <Button
                  key={section.id}
                  variant={activeSection === section.id ? 'default' : 'outline'}
                  onClick={() => setActiveSection(section.id)}
                  className="text-sm"
                >
                  {section.name}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Configurações da Seção */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Fundo */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="w-5 h-5" />
                Fundo
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Tipo de Fundo */}
              <div>
                <label className="text-sm font-medium mb-2 block">Tipo de Fundo</label>
                <div className="grid grid-cols-3 gap-2">
                  <Button
                    variant={currentSectionTheme.backgroundType === 'solid' ? 'default' : 'outline'}
                    onClick={() => updateSectionTheme(activeSection, { backgroundType: 'solid' })}
                    size="sm"
                  >
                    Cor Sólida
                  </Button>
                  <Button
                    variant={currentSectionTheme.backgroundType === 'gradient' ? 'default' : 'outline'}
                    onClick={() => updateSectionTheme(activeSection, { backgroundType: 'gradient' })}
                    size="sm"
                  >
                    Gradiente
                  </Button>
                  <Button
                    variant={currentSectionTheme.backgroundType === 'image' ? 'default' : 'outline'}
                    onClick={() => updateSectionTheme(activeSection, { backgroundType: 'image' })}
                    size="sm"
                  >
                    Imagem
                  </Button>
                </div>
              </div>

              {/* Cor Sólida */}
              {currentSectionTheme.backgroundType === 'solid' && (
                <div>
                  <label className="text-sm font-medium mb-2 block">Cor de Fundo</label>
                  <div className="grid grid-cols-2 gap-2">
                    {BRAND_COLORS.map(color => (
                      <button
                        key={color.value}
                        onClick={() => updateSectionTheme(activeSection, { backgroundColor: color.value })}
                        className={`h-16 rounded border-2 ${
                          currentSectionTheme.backgroundColor === color.value
                            ? 'border-brand-primary ring-2 ring-brand-primary/50'
                            : 'border-slate-200'
                        }`}
                        style={{ backgroundColor: color.value }}
                        title={color.name}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Gradiente */}
              {currentSectionTheme.backgroundType === 'gradient' && (
                <>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Cor 1 do Gradiente</label>
                    <div className="grid grid-cols-2 gap-2">
                      {BRAND_COLORS.map(color => (
                        <button
                          key={color.value}
                          onClick={() => updateSectionTheme(activeSection, { gradientColor1: color.value })}
                          className={`h-12 rounded border-2 ${
                            currentSectionTheme.gradientColor1 === color.value
                              ? 'border-brand-primary ring-2 ring-brand-primary/50'
                              : 'border-slate-200'
                          }`}
                          style={{ backgroundColor: color.value }}
                          title={color.name}
                        />
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Cor 2 do Gradiente</label>
                    <div className="grid grid-cols-2 gap-2">
                      {BRAND_COLORS.map(color => (
                        <button
                          key={color.value}
                          onClick={() => updateSectionTheme(activeSection, { gradientColor2: color.value })}
                          className={`h-12 rounded border-2 ${
                            currentSectionTheme.gradientColor2 === color.value
                              ? 'border-brand-primary ring-2 ring-brand-primary/50'
                              : 'border-slate-200'
                          }`}
                          style={{ backgroundColor: color.value }}
                          title={color.name}
                        />
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Imagem de Fundo */}
              {currentSectionTheme.backgroundType === 'image' && (
                <div>
                  <ImageUpload
                    label="Imagem de Fundo"
                    value={currentSectionTheme.backgroundImage || ''}
                    onChange={(url) => updateSectionTheme(activeSection, { backgroundImage: url })}
                  />
                  
                  <div className="mt-4">
                    <label className="text-sm font-medium mb-2 block">Cor do Overlay</label>
                    <div className="grid grid-cols-2 gap-2">
                      {BRAND_COLORS.map(color => (
                        <button
                          key={color.value}
                          onClick={() => updateSectionTheme(activeSection, { overlayColor: color.value })}
                          className={`h-12 rounded border-2 ${
                            currentSectionTheme.overlayColor === color.value
                              ? 'border-brand-primary ring-2 ring-brand-primary/50'
                              : 'border-slate-200'
                          }`}
                          style={{ backgroundColor: color.value }}
                          title={color.name}
                        />
                      ))}
                    </div>
                    
                    <label className="text-sm font-medium mt-4 mb-2 block">
                      Opacidade do Overlay: {currentSectionTheme.overlayOpacity}%
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={currentSectionTheme.overlayOpacity}
                      onChange={(e) => updateSectionTheme(activeSection, { overlayOpacity: parseInt(e.target.value) })}
                      className="w-full"
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Tipografia */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Type className="w-5 h-5" />
                Tipografia e Cores
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Fonte do Título</label>
                <select
                  value={currentSectionTheme.titleFont}
                  onChange={(e) => updateSectionTheme(activeSection, { titleFont: e.target.value })}
                  className="w-full border rounded p-2"
                >
                  <option value="Playfair Display">Playfair Display (Elegante)</option>
                  <option value="serif">Serif (Clássica)</option>
                  <option value="sans-serif">Sans-serif (Moderna)</option>
                  <option value="Montserrat">Montserrat (Limpa)</option>
                </select>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Fonte do Texto</label>
                <select
                  value={currentSectionTheme.textFont}
                  onChange={(e) => updateSectionTheme(activeSection, { textFont: e.target.value })}
                  className="w-full border rounded p-2"
                >
                  <option value="serif">Serif (Clássica)</option>
                  <option value="sans-serif">Sans-serif (Moderna)</option>
                  <option value="Montserrat">Montserrat (Limpa)</option>
                </select>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Cor do Título</label>
                <div className="grid grid-cols-2 gap-2">
                  {BRAND_COLORS.map(color => (
                    <button
                      key={color.value}
                      onClick={() => updateSectionTheme(activeSection, { titleColor: color.value })}
                      className={`h-12 rounded border-2 ${
                        currentSectionTheme.titleColor === color.value
                          ? 'border-brand-primary ring-2 ring-brand-primary/50'
                          : 'border-slate-200'
                      }`}
                      style={{ backgroundColor: color.value }}
                      title={color.name}
                    />
                  ))}
                  <button
                    onClick={() => updateSectionTheme(activeSection, { titleColor: '#FFFFFF' })}
                    className={`h-12 rounded border-2 bg-white ${
                      currentSectionTheme.titleColor === '#FFFFFF'
                        ? 'border-brand-primary ring-2 ring-brand-primary/50'
                        : 'border-slate-200'
                    }`}
                    title="Branco"
                  >
                    Branco
                  </button>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Cor do Texto</label>
                <div className="grid grid-cols-2 gap-2">
                  {BRAND_COLORS.map(color => (
                    <button
                      key={color.value}
                      onClick={() => updateSectionTheme(activeSection, { textColor: color.value })}
                      className={`h-12 rounded border-2 ${
                        currentSectionTheme.textColor === color.value
                          ? 'border-brand-primary ring-2 ring-brand-primary/50'
                          : 'border-slate-200'
                      }`}
                      style={{ backgroundColor: color.value }}
                      title={color.name}
                    />
                  ))}
                  <button
                    onClick={() => updateSectionTheme(activeSection, { textColor: '#FFFFFF' })}
                    className={`h-12 rounded border-2 bg-white ${
                      currentSectionTheme.textColor === '#FFFFFF'
                        ? 'border-brand-primary ring-2 ring-brand-primary/50'
                        : 'border-slate-200'
                    }`}
                    title="Branco"
                  >
                    Branco
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Botão de Salvar Inferior */}
        <div className="flex justify-end gap-3 pt-6 border-t">
          <Button variant="outline" onClick={handleReset}>
            Resetar Tudo
          </Button>
          <Button onClick={handleSave} className="bg-brand-primary hover:bg-brand-primary-hover text-white">
            Salvar Alterações
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
};

export default ThemeCustomization;
